/**
 * Shared UI component for UI Modal.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';
import { UiButtonComponent } from '../ui-button/ui-button.component';

/**
 * COMPONENT: Ui Modal
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-modal',
  standalone: true,
  imports: [UiButtonComponent, UiLabelTranslatePipe],
  templateUrl: './ui-modal.component.html',
  styleUrl: './ui-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiModalComponent {
  /** PARENT-CHILD CONTRACT: open documents the input state boundary used by this file. */
  readonly open = input(true);
  /** PARENT-CHILD CONTRACT: title documents the input state boundary used by this file. */
  readonly title = input('');
  /** PARENT-CHILD CONTRACT: description documents the input state boundary used by this file. */
  readonly description = input('');
  /** PARENT-CHILD CONTRACT: allowOverflow documents the input state boundary used by this file. */
  readonly allowOverflow = input(false);
  /** PARENT-CHILD CONTRACT: showClose documents the input state boundary used by this file. */
  readonly showClose = input(false);
  /** PARENT-CHILD CONTRACT: maxWidth documents the input state boundary used by this file. */
  readonly maxWidth = input('760px');
  readonly width = input<string | null>(null);
  readonly closed = output<void>();
  readonly closeAction = output<void>();

  /**
   * BUSINESS LOGIC: close
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  close(): void {
    this.closed.emit();
    this.closeAction.emit();
  }
}
